import { saveCustomerApplicationWizard, signIn, signUp } from "@goodfill/backend-api-client";
import { Flex, Main, Paper } from "../../../ui-components";
import { StepIndicator } from "../../account/customerApplication/form/StepIndicator";
import { SignUpForm } from "./SignUpForm";
import { backendFetcher } from "../../backendApi/backendApi";
// import { BreadcrumbNavigation } from "../../BreadcrumbNavigation";
import { useEffect, useState } from "react";
import { FormValidationResult } from "../../../form/formValidation";
import { ConfirmationModal } from "../Confirmation/ConfirmationModal";
import { validSignUpFromState, ValidSignUpFromStateType } from "./signUpFromState";
import { useRecoilState } from "recoil";
import { toastErrorHandler } from "../../../unexpectedErrorHandler";
import { route } from "../../../routes";
import { userAuthState } from "../../../state";
import { useNavigate } from "react-router";
import { CUSTOMER_APPLICATION_STEPS } from "../../account/customerApplication/form/customerApplicationSteps";
import { mapPhonesToApi } from "../../account/customerApplication/mapCustomerApplicationStateToApi";
import { CustomerApplicationWizardSave } from "@goodfill/domain-customer-application-types";
import { UserId } from "@goodfill/domain-properties";
import { Auth } from "aws-amplify";
import { mapLoginDataToUserState } from "../mapLoginDataToUserState";
import { useLogout } from "../../useLogout";

function mapLoginDataToWizardStep1(data: ValidSignUpFromStateType, userId: UserId): CustomerApplicationWizardSave {
  return {
    step: 1,
    userId: `${userId}`,
    wizardSubmission: {
      personalInformation: {
        email: data.email,
        phones: mapPhonesToApi(data),
        name: {
          first: data.firstName,
          last: data.lastName,
        },
      },
    },
  };
}

export const SignUpScreen = () => {
  const [validSignUpFromData] = useRecoilState(validSignUpFromState);
  const [userAuthData, setUserAuthState] = useRecoilState(userAuthState);

  const navigate = useNavigate();

  const [isSend, setIsSend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [backendUsernameErrors, setBackendUsernameErrors] = useState([] as FormValidationResult);
  const [backendPasswordErrors, setBackendPasswordErrors] = useState([] as FormValidationResult);

  useEffect(() => {
    const isUserLoggedIn = userAuthData !== null;
    if (isUserLoggedIn) {
      navigate(route.customerApplication(2));
    }
  }, [userAuthData, navigate]);

  const logout = useLogout();

  const handleSuccess = async (data: ValidSignUpFromStateType) => {
    setIsLoading(true);

    (await signUp(backendFetcher, { username: data.email, password: data.password })).match({
      success: () => {
        setIsSend(true);
      },
      businessMatcher: {
        usernameExists: (e) => setBackendUsernameErrors([e.message]),
        invalidPassword: (e) => setBackendPasswordErrors([e.message]),
      },
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });

    setIsLoading(false);
  };

  const handleOtpSubmit = async () => {
    if (validSignUpFromData === null) {
      return;
    }

    setIsLoading(true);

    const loginData = { username: validSignUpFromData.email, password: validSignUpFromData.password };

    await (
      await signIn(backendFetcher, loginData)
    ).match({
      success: async (loginResponse) => {
        // Todo: remove once we get rid of the Amplify
        try {
          await Auth.signIn(loginData);
          // By setting the auth state, the user data will be refreshed by effect in SecureRouter

          const authData = mapLoginDataToUserState(loginResponse);
          (
            await saveCustomerApplicationWizard(
              backendFetcher,
              authData.idToken,
              mapLoginDataToWizardStep1(validSignUpFromData, authData.user.userId),
            )
          ).match({
            success: () => {
              // redirect happens automatically in useEffect in SignUpScreen components when `userAuthState` changes
              setUserAuthState(authData);
              setIsSend(false);
            },
            businessMatcher: {
              "": () => {},
            },
            unexpected: toastErrorHandler,
            unauthorized: logout,
          });
        } catch (e: unknown) {
          toastErrorHandler(e);
        }
        // -------------------------------------------
      },
      businessMatcher: {
        notAuthorized: toastErrorHandler,
        userNotFound: toastErrorHandler,
        userNotConfirmed: toastErrorHandler,
      },
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });

    setIsLoading(false);
  };

  return (
    <Main>
      {/* <BreadcrumbNavigation items={[]} /> */}

      <Flex verticalMargin={8}>
        <Paper>
          <Flex direction="col" items="center" verticalPadding={12}>
            <StepIndicator currentStep={1} steps={CUSTOMER_APPLICATION_STEPS} />
            {validSignUpFromData != null ? (
              <ConfirmationModal
                isLoading={isLoading}
                isActive={isSend}
                onSuccess={handleOtpSubmit}
                loginData={{ username: validSignUpFromData.email, password: validSignUpFromData.password }}
              />
            ) : null}
            <SignUpForm
              defaultStep1State={{}}
              isLoading={isLoading}
              onSuccess={handleSuccess}
              userNameErrors={backendUsernameErrors}
              passwordErrors={backendPasswordErrors}
            />
          </Flex>
        </Paper>
      </Flex>
    </Main>
  );
};
